import { useMemo, useEffect, useState, useCallback, useRef } from 'react';
import { useBoolean, useToast } from '@chakra-ui/react';
import Swal from 'sweetalert2';

import {
    CreateTransportRequest,
    ScriptDBDeploymentReponse,
    TransportRequestEntity,
    TransportRequestScriptDB,
    TransportRequestSearch,
    TransportRequestTag,
    WebSocketDeploymentRequest,
    WebSocketDeploymentResponse
} from 'data/schemas/TransportRequest';
import { useStore } from 'commons/hooks';
import useLoader from 'hooks/useLoader';
import { getFappPorts } from '../../../data/repositories/monitor/monitorFapp.repository';
import { FappConfig } from '../../../models/FappMonitor';
import {
    createTransportRequestUatV2,
    editOrUpdateTransportRequest,
    getDBConfigEnvs,
    getTransportRequestScriptDB,
    getTransportRequestUatByTag
} from 'data/repositories/TransportRequest';
import JSZip from 'jszip';
import axios from 'axios';
import { addTransportRequestPRD, insertUpdateStatusLog } from 'data/TransportRequest';
import { updateDllVersion } from 'data/ListDll';
import { isJson } from 'commons/helpers';

function TransportRequestUATViewModel() {
    const screenLoader = useLoader();
    const toast = useToast({ duration: 5000, isClosable: true });

    const { transportRequest, authentication } = useStore();
    const citizenId = authentication.getCitizen;
    const { updateTransportRequestResult } = transportRequest;
    const [totalPages, setTotalPage] = useState(1);
    const [isOpenModalDeploy, setOpenModalDeploy] = useState(false);
    const [currentItems, setCurrentItems] = useState<TransportRequestTag[]>();
    const [selectedDeploy, setSelectedDeploy] = useState<TransportRequestTag>({
        uatTag: '',
        details: '',
        developer: '',
        remark: '',
        transportRequestDetail: []
    });
    const [showReadyForPRDModal, setShowReadyForPRDModal] = useBoolean();
    const [showAddTransportRequestModal, setShowAddTransportRequestModal] = useBoolean();
    const [showEditransportRequestModal, setShowEditTransportRequestModal] = useBoolean();
    const [searchHelp, setSearchHelp] = useState<TransportRequestSearch>({
        uatTag: '',
        developer: '',
        deploymentObject: '',
        deployStatus: '',
        page: '1',
        pageSize: '15',
        ticket: ''
    });
    const [fappPorts, setFappPorts] = useState<FappConfig[]>([]);
    const [consoleData, setConsoleData] = useState<WebSocketDeploymentResponse[]>([]);

    const refTransportRequest = useRef<TransportRequestTag>();
    const wsRef = useRef<WebSocket>();
    const connectedRef = useRef<WebSocketDeploymentResponse>();
    const websocketStatusResponse = useMemo(() => ['start', 'on process', 'success'], []);

    useEffect(() => {
        getFappPortOptions();
    }, []);

    useEffect(() => {
        fetchTransportRequest();
    }, [searchHelp.pageSize, searchHelp.page]);

    useEffect(() => {
        if (!isOpenModalDeploy) {
            console.log('Closing websocket');
            closeWebsocket();
            // clear console
            setConsoleData([]);
        }
    }, [isOpenModalDeploy]);

    const toggleModalDeploy = () => setOpenModalDeploy(!isOpenModalDeploy);

    const handlePageChange = ({ selected }) => {
        handleSearchChange('page', selected + 1);
    };

    const handleDeployDll = (deployTag: TransportRequestTag) => {
        setSelectedDeploy(deployTag);
        toggleModalDeploy();
        // updateSelectedDeployStatus(deployTag);
    };

    const fetchTransportRequest = async () => {
        try {
            screenLoader.show();

            await transportRequest.fetchTransportRequestUat(searchHelp);

            if (transportRequest.transportRequestResponse !== null) {
                const resultList = transportRequest.transportRequestResponse.resultList;
                const totalCount = transportRequest.transportRequestResponse.totalCount;
                //handle error slice when no data ja
                if (resultList && totalCount !== undefined) {
                    setCurrentItems(resultList);
                    setTotalPage(Math.ceil(totalCount / parseInt(searchHelp.pageSize)));
                }
            }
        } catch (error) {
            if (error instanceof Error) {
                Swal.fire('Cannot get transport request!', error.message, 'error');
            }
        } finally {
            screenLoader.hide();
        }
    };

    const getFappPortOptions = async () => {
        try {
            // maybe use store for fetch
            const fappPortResultList = await getFappPorts({ isPRD: false });

            const activeFappPorts = [...fappPortResultList].filter(
                port => port.portStatus.toLowerCase() === 'active'
            );

            setFappPorts(activeFappPorts);
        } catch (error) {
            if (error instanceof Error) {
                Swal.fire('Cannot get fapp ports!', error.message, 'error');
            }
        }
    };

    const handleConfirmCreateTR = async (req: CreateTransportRequest) => {
        try {
            screenLoader.show();

            if (
                req.FappFiles.length === 0 &&
                req.FguiFiles.length === 0 &&
                req.ScriptDbFiles.length === 0 &&
                req.CentralFiles.length === 0 &&
                req.ReportFiles.length === 0
            ) {
                const swalAction = await Swal.fire(
                    'Create TR',
                    'Attach files not found.',
                    'warning'
                );

                if (swalAction.isConfirmed) {
                    return;
                }
            }

            await createTransportRequestUatV2(req);
        } catch (error) {
            if (error instanceof Error) {
                Swal.fire('Error', error.message, 'error');
            }
        } finally {
            screenLoader.hide();

            await fetchTransportRequest();
        }
    };

    const handleConfirmDeployTR = async (portList: string[], uatTag: string) => {
        try {
            screenLoader.show();

            updateTransportRequestResult.commandExecutedOutput = [];

            const body = {
                username: authentication.masterEmployeeResponse?.ctizenID ?? '',
                ports: portList.join('|'),
                uatTag
            };

            await transportRequest.updateTransportRequest(body);
        } catch (e) {
            if (e instanceof Error) {
                Swal.fire('Error', e.message, 'error');
            }
        } finally {
            screenLoader.hide();
            await fetchTransportRequest();
        }
    };

    const handleSearchChange = <K extends keyof TransportRequestSearch>(
        key: K,
        value: TransportRequestSearch[K]
    ) => {
        setSearchHelp(prevState => ({
            ...prevState,
            [key]: value
        }));
    };

    const handleShowModalEdit = (transportRequest: TransportRequestTag) => {
        refTransportRequest.current = transportRequest;
        setShowEditTransportRequestModal.on();
    };

    const handleConfirmEditOrUpdateTransportRequest = async (body: TransportRequestTag) => {
        try {
            const response = await editOrUpdateTransportRequest(body);

            if (response === 'success') {
                Swal.fire({
                    title: 'Update',
                    icon: 'success',
                    text: 'ทำการแก้ไขเสร็จสิ้น'
                });
            }
        } catch {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'ไม่สามรถแก้ไขรายการได้'
            });
        } finally {
            fetchTransportRequest();
            setShowEditTransportRequestModal.off();
        }
    };

    const handleFetchGetPathFileByUatTag = async (uatTag: string) => {
        try {
            screenLoader.show();
            const response = await getTransportRequestUatByTag(uatTag);

            if (!response || response.length === 0) {
                return;
            }

            const zipBlob = await createZipFromFiles(response);

            if (zipBlob.size === 0) {
                return;
            } else {
                downloadBlob(zipBlob, uatTag);
            }
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: (error as any).message
            });
        } finally {
            screenLoader.hide();
        }
    };

    const createZipFromFiles = async (fileEntities: TransportRequestEntity[]): Promise<Blob> => {
        const zip = new JSZip();

        for (const entity of fileEntities) {
            if (!entity.s3FilePath) {
                continue;
            }

            const folder = zip.folder(entity.module);

            try {
                const fileData = await downloadFile(entity.s3FilePath);

                if (fileData) {
                    folder?.file(entity.deploymentObject, fileData);
                }
            } catch (error) {
                throw error;
            }
        }

        const zipBlob = await zip.generateAsync({ type: 'blob' });

        return zipBlob;
    };

    const downloadFile = async (url: string): Promise<Blob | undefined> => {
        try {
            const response = await axios.get(url, { responseType: 'blob' });

            return response.data;
        } catch {
            return undefined;
        }
    };

    const downloadBlob = (blob: Blob, filename: string) => {
        if (!blob || blob.size === 0) {
            return;
        }

        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleShowModalReadyForPRD = (uatTag: TransportRequestTag) => {
        setSelectedDeploy(uatTag);
        setShowReadyForPRDModal.on();
    };

    const handleConfirmCreateTRPRD = async (tag: TransportRequestTag) => {
        try {
            screenLoader.show();
            const updateVersionResponse = await updateDllVersion(tag);
            const response = await addTransportRequestPRD(tag);

            if (response === 'success' && updateVersionResponse === 'success') {
                Swal.fire({
                    icon: 'success',
                    title: 'success',
                    text: 'ทำการเตรียมไฟล์ขึ้น PRD เสร็จสิ้น'
                });
            }
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: (error as any).message
            });
        } finally {
            screenLoader.hide();
            fetchTransportRequest();
            setShowReadyForPRDModal.off();
        }
    };

    const fetchScriptDBContents = async () => {
        const results: Array<TransportRequestScriptDB & { error?: Error }> = [];

        try {
            const hasScriptDB = selectedDeploy.transportRequestDetail.some(
                trDetail => trDetail.module.toLowerCase() === 'scriptdb'
            );

            if (!hasScriptDB) {
                return [];
            }

            const scriptDBs = await getTransportRequestScriptDB(selectedDeploy.uatTag);

            for (const scriptDB of scriptDBs) {
                results.push({
                    fileName: scriptDB.fileName,
                    fileContent: scriptDB.fileContent.trim(),
                    status: scriptDB.status,
                    s3Url: scriptDB.s3Url
                });
            }
        } catch (error) {
            results.push({
                fileName: 'Error',
                fileContent: (error as any).message,
                status: 'error',
                s3Url: '',
                error: error as Error
            });
        }

        return results;
    };

    const fetchDatabaseConfigEnvs = async (isProd: boolean) => {
        try {
            const databaseConfigEnvs = await getDBConfigEnvs(isProd);

            return databaseConfigEnvs;
        } catch (error) {
            if (error instanceof Error) {
                Swal.fire(
                    'Cannot get database configuration environments!',
                    error.message,
                    'error'
                );
            }
        }
    };

    const getConnectedDatabases = useCallback(() => {
        if (!connectedRef.current || !connectedRef.current.scriptdb_result) {
            console.log('close connection triggered!');
            return [];
        }

        return [...(connectedRef.current.scriptdb_result.connection_result?.connection_list ?? [])];
    }, [connectedRef.current]);

    const connectToWebSocket = useCallback((payloadRequest: WebSocketDeploymentRequest) => {
        return new Promise<void>((resolve, reject) => {
            const url = window.location.href.includes('local')
                ? 'ws://localhost:8082/ws-deploy-tr'
                : 'wss://ag-management-api.sonarinno.com/ws-deploy-tr';

            wsRef.current = new WebSocket(url);
            const ws = wsRef.current;

            // Listening open connection
            ws.addEventListener('open', _event => {
                if (!payloadRequest.scriptdb_info) {
                    Swal.fire('Error!', 'ScriptDB not found.', 'error');
                    return;
                }

                if (connectedRef.current && connectedRef.current.scriptdb_result) {
                    console.log('connect new databases');
                    const currentConnections =
                        connectedRef.current.scriptdb_result.connection_result?.connection_list ??
                        [];
                    const newConnections = payloadRequest.scriptdb_info.db_targets;

                    // Get current connection that are not in new connections
                    // const dbNameInNewConnections = new Set(newConnections.map(item => item.db_name));
                    // const filteredConnections = currentConnections.filter(item => !dbNameInNewConnections.has(item.db_name));

                    // Get new connection that are not in current connections
                    const dbNameInCurrentConnections = new Set(
                        currentConnections.map(item => item.db_name)
                    );
                    const newConnectionsToAdd = newConnections.filter(
                        item => !dbNameInCurrentConnections.has(item.db_name)
                    );

                    // All requested databases are already connected — skip WebSocket send
                    if (newConnectionsToAdd.length === 0) {
                        console.log('All databases already connected, skipping WebSocket send.');
                        resolve();
                        return;
                    }

                    payloadRequest.scriptdb_info.db_targets = newConnectionsToAdd;
                }

                payloadRequest.scriptdb_info.developer = citizenId ?? '';

                // Send connect to database after connected to websocket
                ws.send(JSON.stringify(payloadRequest));
            });

            // Listening the database is connected or not
            let onMessage: (this: WebSocket, event: MessageEvent<string | any>) => void;
            ws.addEventListener(
                'message',
                (onMessage = event => {
                    // console.log('origin', event.origin);
                    // if (event.origin !== 'http://example.org') {
                    //     // SAFE: origin checked
                    //     return;
                    // }

                    if (!isJson(event.data)) {
                        return;
                    }

                    const data = JSON.parse(event.data) as WebSocketDeploymentResponse;
                    setConsoleData(prev => [...prev, data]);

                    const isConnectDatabaseFailed =
                        !data.scriptdb_result || !data.scriptdb_result.connection_result;

                    // Drop connection
                    // when socket server down or database is not connected
                    if (
                        (!data.status || !websocketStatusResponse.includes(data.status)) &&
                        isConnectDatabaseFailed
                    ) {
                        console.log('connection was rejected!');
                        // ws.close(1000, 'Websocket or Database is not connected.');
                        // delete wsRef.current;
                        closeWebsocket();

                        // Return to caller but doesn't process the rest of the script below
                        reject();

                        return;
                    }

                    if (data.scriptdb_result) {
                        let connections: ScriptDBDeploymentReponse = data.scriptdb_result;

                        if (connectedRef.current) {
                            // combine the connections
                            connections = {
                                ...connectedRef.current.scriptdb_result,
                                ...data.scriptdb_result
                            };
                        }

                        // Save connection connections
                        connectedRef.current = {
                            message: '',
                            status: '',
                            scriptdb_result: connections
                        };
                    }

                    if (data.status === 'success') {
                        // Show success toast
                        toast({ title: 'Connected to server success!', duration: 6000 });

                        // Return to caller but still processing the rest of the script below
                        resolve();

                        // Remove this listener (Connection state)
                        ws.removeEventListener('message', onMessage);
                    }
                })
            );
        });
    }, []);

    const handleClickConnectDatabase = useCallback(
        async (connection: WebSocketDeploymentRequest) => {
            try {
                screenLoader.show();

                await connectToWebSocket(connection);
            } catch (error) {
                if (error instanceof Error) {
                    Swal.fire('Error!', error.message, 'error');
                }
            } finally {
                screenLoader.hide();
            }
        },
        []
    );

    const closeWebsocket = () => {
        if (!wsRef.current) return;

        const ws = wsRef.current;
        ws.close(1000, 'Closing websocket');
        delete wsRef.current;
        delete connectedRef.current;
    };

    const handleClickRunQuery = useCallback((request: WebSocketDeploymentRequest) => {
        if (!request.scriptdb_info) {
            Swal.fire('Error', 'กรุณาเลือก ScriptDB ที่ต้องการรัน', 'error');
            return;
        }
        request.scriptdb_info.developer = citizenId ?? '';
        runQuery(request);
    }, []);

    const runQuery = async (request: WebSocketDeploymentRequest) => {
        const ws = wsRef.current;
        if (!ws) {
            Swal.fire('Error', 'ไม่สามารถเชื่อมต่อ Server ได้ กรุณาเชื่อมต่อใหม่อีกครั้ง', 'error');

            return;
        }

        const connection = connectedRef.current;
        if (!connection) {
            Swal.fire(
                'Error',
                'ไม่สามารถเชื่อมต่อกับฐานข้อมูล กรุณาเชื่อมต่อใหม่อีกครั้ง',
                'error'
            );

            return;
        }

        let workingRequest: WebSocketDeploymentRequest | undefined;
        let resolver: () => void;

        // Listen script message sequentially
        let onMessage: (this: WebSocket, event: MessageEvent<string>) => void;
        ws.addEventListener(
            'message',
            (onMessage = async (event: MessageEvent<string>) => {
                if (!isJson(event.data)) {
                    return;
                }

                const data = JSON.parse(event.data) as WebSocketDeploymentResponse;
                setConsoleData(prevState => [...prevState, data]);

                if (data.status === 'error') {
                    toast({
                        colorScheme: 'red',
                        description: data.message,
                        duration: 5000
                    });

                    resolver();
                }

                if (data.scriptdb_result && data.scriptdb_result.execute_result) {
                    const result = data.scriptdb_result.execute_result;
                    // ws.send(JSON.stringify());
                }
            })
        );

        // Send scriptdb info to server
        if (request.scriptdb_info) {
            request.scriptdb_info.developer = citizenId ?? '';

            // Jump to `onMessage` function
            await new Promise<void>(resolve => {
                resolver = resolve;

                ws.send(JSON.stringify((workingRequest = request)));

                // Statement will be here until `resolve` is called
            });
        }

        console.log('remove event message...');
        ws.removeEventListener('message', onMessage);
    };

    const updateSelectedDeployStatus = async (
        selectedDeploy: TransportRequestTag,
        company: string[]
    ) => {
        try {
            screenLoader.show();

            selectedDeploy.transportRequestDetail.map(item => {
                item.deployStatus = 'Online';
            });

            const response = await editOrUpdateTransportRequest(selectedDeploy);
            updateStatusLog(selectedDeploy, company);
        } catch (error) {
            if (error instanceof Error) {
                Swal.fire('Error!', error.message, 'error');
            }
        } finally {
            screenLoader.hide();
        }
    };

    const updateStatusLog = async (selectedDeploy: TransportRequestTag, company: string[]) => {
        try {
            screenLoader.show();

            selectedDeploy.updatedBy = authentication.masterEmployeeResponse?.ctizenID ?? '';
            selectedDeploy.deployCompany = company;

            const response = await insertUpdateStatusLog(selectedDeploy);

            if (response === 'success') {
                Swal.fire({
                    title: 'Update',
                    icon: 'success',
                    text: 'deploy status updated successfully'
                });
            }
        } catch (error) {
            if (error instanceof Error) {
                Swal.fire('Error!', error.message, 'error');
            }
        } finally {
            await toggleModalDeploy();
            await fetchTransportRequest();
            screenLoader.hide();
        }
    };

    return {
        fappPorts,
        totalPages,
        currentItems,
        selectedDeploy,
        isOpenModalDeploy,
        refTransportRequest,
        showReadyForPRDModal,
        showAddTransportRequestModal,
        showEditransportRequestModal,
        getConnectedDatabases,
        consoleData,

        handleDeployDll,
        handlePageChange,
        toggleModalDeploy,
        handleSearchChange,
        handleClickRunQuery,
        handleShowModalEdit,
        fetchScriptDBContents,
        fetchTransportRequest,
        handleConfirmCreateTR,
        handleConfirmDeployTR,
        fetchDatabaseConfigEnvs,
        setShowReadyForPRDModal,
        handleConfirmCreateTRPRD,
        updateSelectedDeployStatus,
        handleClickConnectDatabase,
        handleShowModalReadyForPRD,
        updateTransportRequestResult,
        handleFetchGetPathFileByUatTag,
        setShowAddTransportRequestModal,
        setShowEditTransportRequestModal,
        handleConfirmEditOrUpdateTransportRequest
    };
}

export default TransportRequestUATViewModel;
